import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import moment from "moment";
import api from "../services/api";
import ApiHubBackend from "../services/ApiHubBackend";
import apiPortalTransportador from "../services/apiPortalTransportador";
import {
  attachAuthInterceptor,
  clearAuthStorage,
  onTokenRefreshed,
  TOKEN_KEY,
  REFRESH_KEY,
  REFRESH_EXP_KEY,
  USER_KEY,
} from "../services/authRefresh";

const AuthContext = createContext({});

// Mesma chave do hooks/companies.js (@HubEverlog:companySelection).
const COMPANY_SELECTION_KEY = "@HubEverlog:companySelection";
function clearCompanySelection() {
  try {
    localStorage.removeItem(COMPANY_SELECTION_KEY);
  } catch (_) {
    // storage indisponível — ignora
  }
}

// Anexa o interceptor de refresh nas três instâncias logo no carregamento do
// módulo. Idempotente porque o módulo só é avaliado uma vez.
[api, ApiHubBackend, apiPortalTransportador].forEach((instance) => {
  attachAuthInterceptor(instance);
});

function setAuthHeaders(token) {
  // axios 0.19 não normaliza case do header. Padronizar tudo em
  // `Authorization` (capital) e apagar a variante minúscula antes de setar,
  // senão o retry pós-refresh pode mandar o token velho.
  const instances = [api, ApiHubBackend, apiPortalTransportador];
  if (token) {
    instances.forEach((inst) => {
      delete inst.defaults.headers.authorization;
      inst.defaults.headers.Authorization = `Bearer ${token}`;
    });
  } else {
    instances.forEach((inst) => {
      delete inst.defaults.headers.authorization;
      delete inst.defaults.headers.Authorization;
    });
  }
}

// Decide se a sessão guardada no storage ainda vale.
//   - Se o REFRESH ainda está dentro da validade, mantém a sessão. O access
//     pode estar expirado — o interceptor renova na primeira chamada.
//   - Sem dado de refresh (ex.: storage de versão antiga), cai no comportamento
//     legado de comparar só o exp do access.
function readSessionFromStorage() {
  const token = localStorage.getItem(TOKEN_KEY);
  const userRaw = localStorage.getItem(USER_KEY);
  if (!token || !userRaw) return null;

  let userParsed;
  try {
    userParsed = JSON.parse(userRaw);
  } catch (_) {
    return null;
  }

  const refreshExpRaw = localStorage.getItem(REFRESH_EXP_KEY);
  if (refreshExpRaw) {
    const refreshExp = moment(refreshExpRaw);
    if (refreshExp.isValid() && moment().isSameOrAfter(refreshExp)) {
      return null;
    }
    return { token, user: userParsed };
  }

  // Backward-compat: storage antigo sem refresh.
  if (userParsed && userParsed.exp) {
    const tokenExpiration = moment.unix(userParsed.exp);
    if (moment().isSameOrAfter(tokenExpiration)) {
      return null;
    }
  }
  return { token, user: userParsed };
}

export const AuthProvider = ({ children }) => {
  const [data, setData] = useState(() => {
    const session = readSessionFromStorage();
    if (!session) {
      clearAuthStorage();
      return {};
    }
    setAuthHeaders(session.token);
    return session;
  });

  // Quando o interceptor renova o token, sincroniza state + headers de todas
  // as instâncias.
  useEffect(() => {
    const off = onTokenRefreshed((newToken) => {
      setAuthHeaders(newToken);
      setData((prev) => (prev && prev.user ? { ...prev, token: newToken } : prev));
    });
    return off;
  }, []);

  // PO1-1918: hidrata avatarUrl + name/surname do backend após o login
  // (esses campos não vêm no payload do POST /sessions; o avatar do header
  // dependeria do user navegar até a tela de Perfil pra aparecer).
  const userId = data && data.user && data.user.id;
  useEffect(() => {
    if (!userId) return undefined;
    let cancelled = false;
    api
      .get("/users/me")
      .then((response) => {
        if (cancelled) return;
        setData((prev) => {
          if (!prev || !prev.user) return prev;
          const nextUser = {
            ...prev.user,
            avatarUrl: response.data.avatarUrl,
            name: response.data.name || prev.user.name,
            surname: response.data.surname || prev.user.surname,
            // PO1-2299: marca "em habilitação" da transportadora, calculada
            // na hora pelo backend (não vem do token antigo).
            ...(response.data.emHabilitacao !== undefined
              ? { emHabilitacao: response.data.emHabilitacao }
              : {}),
            // PO1-2287: preferência de produto (o activeProduct lê daqui) e a
            // transportadora do login (PO1-2286: fixa no topo do MeuFrete).
            ...(response.data.produtoPadrao !== undefined
              ? { produtoPadrao: response.data.produtoPadrao }
              : {}),
            ...(response.data.transportadoraId
              ? {
                  transportadoraId: response.data.transportadoraId,
                  transportadoraNome: response.data.transportadoraNome || prev.user.transportadoraNome || null,
                }
              : {}),
          };
          try {
            localStorage.setItem(USER_KEY, JSON.stringify(nextUser));
          } catch (_) {
            // localStorage cheio / desativado — ignora
          }
          return { ...prev, user: nextUser };
        });
      })
      .catch(() => {
        // Falha silenciosa — header continua com fallback de iniciais.
      });
    return () => {
      cancelled = true;
    };
  }, [userId]);

  const login = useCallback(async ({ username, password }) => {
    const response = await api.post("sessions", {
      username,
      password,
    });

    const { token, user, refreshToken, refreshExpiresAt } = response.data;

    // PO1-2286: a seleção de empresa é de quem logou antes neste navegador.
    // Um embarcador fora do conjunto do novo login viraria 403 nas telas de
    // dados (PO1-3102), então ela começa limpa a cada login.
    clearCompanySelection();

    localStorage.setItem(TOKEN_KEY, token);
    if (refreshToken) {
      localStorage.setItem(REFRESH_KEY, refreshToken);
    }
    if (refreshExpiresAt) {
      const iso =
        refreshExpiresAt instanceof Date
          ? refreshExpiresAt.toISOString()
          : new Date(refreshExpiresAt).toISOString();
      localStorage.setItem(REFRESH_EXP_KEY, iso);
    }

    setAuthHeaders(token);

    if (user) {
      const permission = await apiPortalTransportador.get(
        `/UsersFlow/permissionHub/${user.id}`
      );

      // PO1-2299: a identidade provisória da transportadora (emHabilitacao)
      // entra mesmo sem permissão — quem bloqueia a operação é a trava no
      // servidor, e o painel de habilitação não depende de permissão.
      const permData = permission.status === 200 && Array.isArray(permission.data) ? permission.data : [];
      if (permData.length > 0 || user.emHabilitacao) {
        const newUser = { ...user, permissions: permData };
        localStorage.setItem(USER_KEY, JSON.stringify(newUser));
        setData({ token, user: newUser });
      }
    }
  }, []);

  // PO1-2299/PO1-3106: relê /users/me e aplica no user em memória. O painel de
  // habilitação chama quando o backend diz emHabilitacao=false (aprovada) para
  // sair do painel sem novo login — o refresh de token não atualiza o user.
  const refreshUser = useCallback(async () => {
    const response = await api.get("/users/me");
    const partial = {
      avatarUrl: response.data.avatarUrl,
      name: response.data.name,
      surname: response.data.surname,
      ...(response.data.emHabilitacao !== undefined
        ? { emHabilitacao: response.data.emHabilitacao }
        : {}),
    };
    setData((prev) => {
      if (!prev || !prev.user) return prev;
      const nextUser = { ...prev.user, ...partial };
      try {
        localStorage.setItem(USER_KEY, JSON.stringify(nextUser));
      } catch (_) {
        // localStorage cheio / desativado — ignora
      }
      return { ...prev, user: nextUser };
    });
    return partial;
  }, []);

  // PO1-1918: permite componentes (tela de Perfil) atualizarem campos do
  // user após salvar — header reage sem precisar de reload.
  const updateUser = useCallback((partial) => {
    setData((prev) => {
      if (!prev || !prev.user) return prev;
      const nextUser = { ...prev.user, ...(partial || {}) };
      try {
        localStorage.setItem(USER_KEY, JSON.stringify(nextUser));
      } catch (_) {
        // localStorage cheio / desativado — ignora; estado em memória ainda fica certo
      }
      return { ...prev, user: nextUser };
    });
  }, []);

  const logout = useCallback(async () => {
    const refreshToken = localStorage.getItem(REFRESH_KEY);
    if (refreshToken) {
      try {
        await api.post("sessions/logout", { refreshToken });
      } catch (_) {
        // Best-effort: se a chamada falhar, segue limpando o estado local
        // pra não bloquear o logout do usuário.
      }
    }
    clearAuthStorage();
    clearCompanySelection();
    setAuthHeaders(null);
    setData({});
  }, []);

  return (
    <AuthContext.Provider value={{ user: data.user, login, logout, updateUser, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
};

// hook de autenticação
export const useAuth = () => {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
